import React, { useEffect, useRef } from 'react';
import './Modal.css'; // Ensure you have appropriate styling
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const ModalGeneric = ({ title, children, isOpen, onClose, dismissable = true }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);

  // Focus the modal when it opens for screen readers
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key for WCAG 2.1.2 and focus trap for WCAG 2.4.3
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && dismissable) {
        onClose();
        return;
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, dismissable]);

  if (!isOpen) {
    return null; // Do not render the modal if it's not open
  }

  return (
    <div className="modal-overlay" onClick={dismissable ? onClose : undefined}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="modal-content">
          <div className="modal-header">
            <span id="modal-title">{title}</span>
            {dismissable && (
              <button
                onClick={onClose}
                className="modal-close-button"
                aria-label={t('close_modal') || 'Close dialog'}
              >
                <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="modal-body">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalGeneric;